import { Injectable, EventEmitter } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { SkihirePoiResponseInterface } from '../interfaces/skihire-poi-response-interface';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';

@Injectable({
  providedIn: 'root'
})
export class PoiService {
  private searchText$ = new Subject<string>();
  private culture: string;
  private lastResult: SkihirePoiResponseInterface;
  private refererId: string;
  private sub: Subscription;

  /**
   * will return true if a new phrase is accepted (after debounce)
   * will return false directly before results are returned by onPoiChange
   */
  readonly onSearching: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * is fired everytime a new phrase leads to a new result (after debounce)
   */
  readonly onPoiChange: EventEmitter<SkihirePoiResponseInterface> =
    new EventEmitter<SkihirePoiResponseInterface>();

  constructor(
    private apiService: ApiService,
    private translateService: TranslateService,
    private platformService: PlatformInformationService
  ) {
    // set culture and update on app language changes
    this.culture = translateService.currentLang;
    this.translateService.onLangChange.subscribe((lang: LangChangeEvent) => {
      this.culture = lang.lang;
      // TODO: maybe we need to update the searchresults
    });

    this.prepSearchText();
  }

  setReferer(refererId: string) {
    this.refererId = refererId;
    this.prepSearchText();
  }

  private prepSearchText() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.platformService.getIsBrowser()) {
      this.sub = this.searchText$
        .pipe(
          debounceTime(400), // wait for key inputs to prevent 1000 searches while typing
          distinctUntilChanged(), // prevent duplicate searches
          switchMap(
            // switch to new observable from api
            newPhrase => {
              this.onSearching.emit(true);
              return this.apiService.getSkihirePois(newPhrase, this.culture, this.refererId);
            }
          )
        )
        .subscribe(response => {
          this.onSearching.emit(false);
          this.onPoiChange.emit(response);
          this.lastResult = response;
        });
    } else {
      // server has no switchmap
      this.sub = this.searchText$.subscribe(text => {
        this.apiService.getSkihirePois(text, this.culture, this.refererId).subscribe(response => {
          this.onSearching.emit(false);
          this.onPoiChange.emit(response);
          this.lastResult = response;
        });
      });
    }
  }

  /**
   * updates the current search phrase.
   * Will lead to onSearching and onPoiChange Events
   * @param phrase a search phrase
   */
  public updatePhrase(phrase: string) {
    this.searchText$.next(phrase);
  }

  getLastResult(): SkihirePoiResponseInterface {
    return this.lastResult;
  }
}
